import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tab, Tabs } from '@material-ui/core';
import { WORKFLOW_STAGES } from '../../mocks/executionData';
import { useStyles } from '../../styles';
import '../../styles/projectCard.css';
import '../../styles/executionCenter.css';
import type { WorkflowStage } from '../../interfaces/ui';
import { useActivityLog } from '../../hooks/useActivityLog';
import { useExecutionResults } from '../../hooks/useExecutionResults';
import { useExecutionTestCases } from '../../hooks/useExecutionTestCases';
import ProjectContextHeader from './ProjectContextHeader';
import ExecutionStepper from './ExecutionStepper';
import ExecutionResultsTab from './ExecutionResultsTab';
import ActivityLogTab from './ActivityLogTab';
import TestCasesTab from './TestCasesTab';

type ArtifactTab = 'activity' | 'results' | 'cases';

interface ExecutionCenterProps {
  isOpen?: boolean;
  mode?: string;
  apmNumber: string;
  projectName: string;
  projectId: string;
  runId?: string;
  environment: string;
  testPlanName: string;
  onClose: () => void;
  onViewDetailedResults?: (projectId: string) => void;
}

export const ExecutionCenter = ({
  isOpen = true,
  apmNumber,
  projectName,
  projectId,
  runId,
  environment,
  testPlanName,
  onClose,
  onViewDetailedResults,
}: ExecutionCenterProps) => {
  const [activeTab, setActiveTab] = useState<ArtifactTab>('activity');
  const [isActivityComplete, setIsActivityComplete] = useState(false);
  const [workflowStages] = useState<WorkflowStage[]>(() =>
    WORKFLOW_STAGES.map(stage => ({
      ...stage,
      status: stage.id === '1' ? 'active' : 'pending',
    })),
  );
  const fetchedResultsRef = useRef<string | null>(null);
  const fetchedCasesRef = useRef<string | null>(null);
  const activityLog = useActivityLog();
  const executionResults = useExecutionResults();
  const executionTestCases = useExecutionTestCases();
  const classes = useStyles();

  useEffect(() => {
    if (!isOpen || !projectId || !runId) return;

    setIsActivityComplete(false);
    fetchedResultsRef.current = null;
    fetchedCasesRef.current = null;
    setActiveTab('activity');
    activityLog.reset();
    executionResults.reset();
    executionTestCases.reset();
    activityLog.streamActivityLog(
      { apmNumber, projectId, runId, type: 'activity' },
      () => {
        setIsActivityComplete(true);
        setActiveTab('results');
      },
    );

    return activityLog.reset;
  }, [
    activityLog.reset,
    activityLog.streamActivityLog,
    apmNumber,
    executionResults.reset,
    executionTestCases.reset,
    isOpen,
    projectId,
    runId,
  ]);

  useEffect(() => {
    if (activeTab !== 'cases') {
      fetchedCasesRef.current = null;
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      activeTab !== 'results' ||
      !projectId ||
      !runId ||
      !isActivityComplete ||
      fetchedResultsRef.current === runId
    ) {
      return;
    }

    fetchedResultsRef.current = runId;
    executionResults.fetchResults({
      apmNumber,
      projectId,
      runId,
      type: 'dashboard',
    });
  }, [
    activeTab,
    apmNumber,
    executionResults.fetchResults,
    isActivityComplete,
    projectId,
    runId,
  ]);

  useEffect(() => {
    if (
      activeTab !== 'cases' ||
      !projectId ||
      !runId ||
      !isActivityComplete ||
      executionTestCases.data.length > 0 ||
      fetchedCasesRef.current === runId
    ) {
      return;
    }

    fetchedCasesRef.current = runId;
    executionTestCases.fetchTestCases({
      apmNumber,
      projectId,
      runId,
      type: 'testplan',
    });
  }, [
    activeTab,
    apmNumber,
    executionTestCases.data.length,
    executionTestCases.fetchTestCases,
    isActivityComplete,
    projectId,
    runId,
  ]);

  const refreshActivityLog = useCallback(() => {
    if (!projectId || !runId) return;
    activityLog.streamActivityLog(
      { apmNumber, projectId, runId, type: 'activity' },
      () => {
        setIsActivityComplete(true);
        setActiveTab('results');
      },
    );
  }, [activityLog.streamActivityLog, apmNumber, projectId, runId]);

  if (!isOpen) return null;

  return (
    <main>
      {/* <Grid item xs={12} sm={12} md={12} lg={12}>
        <BackButton />
        <HelpComponent helpUrl={HELP_URL.PERFORMANCE_TEST_RUNS_PAGE} />
      </Grid> */}
      <Box className={`ec-center ${classes.executionCenter}`}>
        <ProjectContextHeader
          projectName={projectName}
          environment={environment}
          testPlanName={testPlanName}
        />
        <ExecutionStepper workflowStages={workflowStages} />
        <Box className="ec-content">
          <Box className="ec-artifacts-section">
            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value as ArtifactTab)}
              indicatorColor="primary"
              textColor="primary"
              className="ec-tabs"
            >
              <Tab label="Activity Log" value="activity" className="ec-tab" />
              <Tab label="Results" value="results" className="ec-tab" />
              <Tab label="Test Cases" value="cases" className="ec-tab" />
            </Tabs>

            {activeTab === 'activity' && (
              <ActivityLogTab
                activityLog={activityLog.data}
                loading={activityLog.loading}
                error={activityLog.error}
                onRefresh={refreshActivityLog}
              />
            )}
            {activeTab === 'results' && (
              <ExecutionResultsTab
                data={executionResults.data}
                loading={executionResults.loading}
                error={executionResults.error}
                projectId={projectId}
                onClose={onClose}
                onViewDetailedResults={onViewDetailedResults}
              />
            )}
            {activeTab === 'cases' && (
              <TestCasesTab
                testSuites={executionTestCases.data}
                loading={executionTestCases.loading}
                error={executionTestCases.error}
              />
            )}
          </Box>
        </Box>
      </Box>
    </main>
  );
};
